// import React, { useMemo, useState, useEffect, lazy, Suspense } from 'react';
import React, { useMemo, useState, useEffect } from 'react';
import logo from '../../images/logo-blue.png'
import TextInput from '../../helpers/TextInput';
import { useProjectDetailsForm } from '../../helpers/UseProjectDetailsForm';
import { handleButtonClick } from '../../helpers/HandleDownloadRequest';
import SelectedInput from '../../helpers/SelectedInput';
import logService from '../../services/logService';

const GeneratorForm = () => {


  const initialState = {
    connectorName: 'ConfluentConnectorExample',
    packageName: 'io.confluent.connector.example',
    artifactId: 'connector-example',
    groupId: 'io.confluent',
    projectVersion: '1.0.0-SNAPSHOT',
    description: 'An Example Kafka Connector Project',
    author: 'Confluent',
    connectorType: '',
    connectorLanguageType: '',
    javaVersion: '21',
    buildType: 'maven',
    kafkaVersion: '4.2.0',
    mavenVersion: '3.9.8',
    license: 'apache2',
    topics: 'exampleTopic1,exampleTopic2'
  };

  const [projectDetails, handleInputChange, resetForm] = useProjectDetailsForm(initialState);
  const [featureFlags, setFeatureFlags] = useState({ pythonConnectors: { enabled: false } });

  // Track which fields have been touched (blurred)
  const [touchedFields, setTouchedFields] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Fetch feature flags on mount
  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        const response = await fetch('/v1/feature-flags');
        if (response.ok) {
          const flags = await response.json();
          setFeatureFlags(flags);
        }
      } catch (error) {
        logService.error('Failed to fetch feature flags: ' + error.message);
        // Keep default flags (Python disabled) on error
      }
    };
    fetchFeatureFlags();
  }, []);

  // Clear Python selection if connector type changes to sink
  useEffect(() => {
    if (projectDetails.connectorType === 'sink' && projectDetails.connectorLanguageType === 'python') {
      handleInputChange({ target: { id: 'connectorLanguageType', value: '' } });
    }
  }, [handleInputChange, projectDetails.connectorLanguageType, projectDetails.connectorType]);

  // Set appropriate version defaults when language type changes
  useEffect(() => {
    if (projectDetails.connectorLanguageType === 'python') {
      // Set Python version to 3.13 for Python connectors
      if (projectDetails.javaVersion !== '3.13') {
        handleInputChange({ target: { name: 'javaVersion', value: '3.13' } });
      }
      if (projectDetails.kafkaVersion !== '4.2.0') {
        handleInputChange({ target: { name: 'kafkaVersion', value: '4.2.0' } });
      }
    } else if (projectDetails.connectorLanguageType === 'java' && !['21', '17', '11', '1.8'].includes(projectDetails.javaVersion)) {
      // Reset to Java defaults when switching back to Java
      handleInputChange({ target: { name: 'javaVersion', value: '21' } });
    }
  }, [handleInputChange, projectDetails.connectorLanguageType, projectDetails.javaVersion, projectDetails.kafkaVersion]);

  // Field name to user-friendly label mapping
  const fieldLabels = {
    connectorName: 'Connector Name',
    packageName: 'Package Name',
    artifactId: 'Artifact ID',
    groupId: 'Group ID',
    projectVersion: 'Project Version',
    description: 'Description',
    author: 'Author',
    connectorType: 'Connector Type',
    connectorLanguageType: 'Language',
    javaVersion: 'Java Version',
    buildType: 'Build Type',
    kafkaVersion: 'Kafka Version',
    mavenVersion: 'Maven Version',
    license: 'License'
  };

  const requiredFields = useMemo(() => {
    // For Python source connectors, only basic fields and additional information are required
    if (projectDetails.connectorType === 'source' && projectDetails.connectorLanguageType === 'python') {
      return ['connectorName', 'connectorType', 'connectorLanguageType', 'description', 'author', 'license'];
    }

    const baseFields = ['connectorName', 'packageName', 'artifactId', 'groupId',
      'description', 'author', 'connectorType', 'connectorLanguageType', 'javaVersion', 'kafkaVersion', 'license'];
    // Only include projectVersion, buildType, and mavenVersion if language is not Python
    if (projectDetails.connectorLanguageType !== 'python') {
      baseFields.push('projectVersion');
      baseFields.push('buildType');
      baseFields.push('mavenVersion');
    }
    return baseFields;
  }, [projectDetails.connectorType, projectDetails.connectorLanguageType]);

  const { areDetailsComplete, missingFields } = useMemo(() => {
    const missing = requiredFields.filter(field => !projectDetails[field] || projectDetails[field].trim() === '');
    return {
      areDetailsComplete: missing.length === 0,
      missingFields: missing,
    };
  }, [projectDetails, requiredFields]);

  const isFieldEmpty = (fieldName) => !projectDetails[fieldName] || projectDetails[fieldName].trim() === '';

  // Only show validation error if field has been touched or submit was attempted
  const shouldShowError = (fieldName) => {
    return (touchedFields[fieldName] || submitAttempted) && isFieldEmpty(fieldName);
  };

  // Handle field blur to mark as touched
  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  };

  // Handle form reset
  const handleReset = () => {
    resetForm();
    setTouchedFields({});
    setSubmitAttempted(false);
    setIsGenerating(false);
    setDownloadStatus(null);
    setErrorMessage('');
  };

  // Handle showing confirmation dialog
  const handleGenerateClick = (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (areDetailsComplete) {
      setShowConfirmation(true);
    }
  };

  // Handle confirming the download
  const handleConfirmDownload = (e) => {
    setShowConfirmation(false);
    handleButtonClick(projectDetails, e, setIsGenerating, setDownloadStatus, setErrorMessage);
  };

  // Handle canceling the download
  const handleCancelDownload = () => {
    setShowConfirmation(false);
  };

  const [staging] = useState(false); // The banner is visible by default since we are staging.
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null); // 'success', 'error', null
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Calculate form progress - only completes when step is done
  const formProgress = useMemo(() => {
    let stepLabel = 'Select Connector Type';
    let nextSteps = 'Choose between Sink or Source connector';
    let progressPercent = 0;

    if (projectDetails.connectorType) {
      stepLabel = 'Select Language';
      nextSteps = 'Choose your programming language';
      progressPercent = 33;
    }

    if (projectDetails.connectorLanguageType) {
      stepLabel = 'Complete Project Configuration';
      nextSteps = 'Fill in all required fields below';
      progressPercent = 66;
    }

    if (areDetailsComplete) {
      stepLabel = 'Ready to Generate';
      nextSteps = 'All fields complete - click "Confirm Details" to continue';
      progressPercent = 100;
    }

    return { stepLabel, nextSteps, progressPercent };
  }, [projectDetails.connectorType, projectDetails.connectorLanguageType, areDetailsComplete]);

  return (
    <div >
    <div>{staging && (<div className="banner">TEST ENV - This is a test website and is subject to change.</div>)}</div>
      <div className="confluent-main-wrapper">
        <div className="confluent-form-wrapper">
          <form>
            <div className="confluent-form-title">
              <div className="title-with-logo">
                <img
                  src={logo}
                  className="logo-icon"
                  alt='Confluent Logo'
                  loading="lazy"
                  decoding="async"
                />
                <h2>Confluent Connector Initializer</h2>
              </div>
              <div className="title-with-buttons">
                <p>Generate a new Apache Kafka® connector in just a few seconds!</p>
                <div className="button-group">
                  <button onClick={() => window.open('https://didactic-adventure-mzk421w.pages.github.io/', '_blank')} title="Documentation" rel="noopener noreferrer">
                    <img src="icon-book.svg" style={{ height: '70%', width: '70%' }} alt="Documentation" />
                  </button>
                  <button onClick={() => window.open('https://forms.gle/ZbnDypWs2CjK8ErMA', '_blank')} title="Add Feedback" rel="noopener noreferrer">
                    <img src="icon-quotes.svg" style={{ height: '70%', width: '70%' }} alt="Add Feedback" />
                  </button>
                </div>
              </div>
              <br />
            </div>

            {/* Progress Indicator */}
            <div className="form-progress-container">
              <div className="progress-header">
                <span className="progress-step-label">{formProgress.stepLabel}</span>
                <span className="progress-percentage">{formProgress.progressPercent}%</span>
              </div>
              <div className="progress-bar-track">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${formProgress.progressPercent}%` }}
                ></div>
              </div>
              <p className="progress-next-steps">{formProgress.nextSteps}</p>
            </div>

            {/* SECTION 1: Basic Configuration */}
            <div className="form-section">
              <div className="form-section-header">
                <div>
                  <h3 className="form-section-title">Basic Configuration</h3>
                  <p className="form-section-description">Define your connector's core settings</p>
                </div>
              </div>
              <div className="form-section-content">
                <TextInput
                  label="Connector Name *"
                  name="connectorName"
                  placeholder='ConfluentConnectorExample'
                  value={projectDetails.connectorName}
                  onChange={handleInputChange}
                  onBlur={() => handleFieldBlur('connectorName')}
                  showError={shouldShowError('connectorName')}
                  showSuccess={false}
                  errorMessage="Connector Name is required"
                  tooltip="A descriptive name for your connector (e.g., MySQLSourceConnector or S3SinkConnector)"
                />
                <SelectedInput
                  label="Connector Type *"
                  id="connectorType"
                  value={projectDetails.connectorType}
                  onChange={handleInputChange}
                  onBlur={() => handleFieldBlur('connectorType')}
                  options={[
                    { value: "", disabled: true, label: "Select Connector Type" },
                    { value: "sink", label: "Sink" },
                    { value: "source", label: "Source" }
                  ]}
                  showError={shouldShowError('connectorType')}
                  showSuccess={false}
                  errorMessage="Connector Type is required"
                />
                <p className='small-muted-text'>
                  <strong>Source:</strong> Reads data from external systems and sends to Kafka topics<br />
                  <strong>Sink:</strong> Reads data from Kafka topics and sends to external systems
                </p>

                {projectDetails.connectorType !== '' && (
                  <SelectedInput
                    id="connectorLanguageType"
                    label="Language *"
                    value={projectDetails.connectorLanguageType}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('connectorLanguageType')}
                    options={[
                      { value: "", disabled: true, label: "Select Language" },
                      { value: "java", label: "Java" },
                      ...(projectDetails.connectorType === 'source' && featureFlags.pythonConnectors.enabled ? [{ value: "python", label: "Python" }] : [])
                    ]}
                    showError={shouldShowError('connectorLanguageType')}
                    showSuccess={false}
                    errorMessage="Language is required"
                  />
                )}

                {projectDetails.connectorType === 'sink' && (
                  <TextInput
                    label="Topics"
                    name="topics"
                    placeholder="Topic Name(s) e.g Topic1,Topic2"
                    value={projectDetails.topics}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('topics')}
                    showError={false}
                    showSuccess={false}
                    tooltip="Kafka topics that this sink connector will read from (comma-separated for multiple topics)"
                  />
                )}
              </div>
            </div>

            {/* SECTION 2: Project Details */}
            {projectDetails.connectorLanguageType && !(projectDetails.connectorType === 'source' && projectDetails.connectorLanguageType === 'python') && (
              <div className="form-section">
                <div className="form-section-header">
                  <div>
                    <h3 className="form-section-title">Project Details</h3>
                    <p className="form-section-description">Configure your project's identity and metadata</p>
                  </div>
                </div>
                <div className="form-section-content">
                  <TextInput
                    label="Package Name *"
                    name="packageName"
                    placeholder="io.confluent.example.connect"
                    value={projectDetails.packageName}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('packageName')}
                    showError={shouldShowError('packageName')}
                    showSuccess={false}
                    errorMessage="Package Name is required"
                    tooltip="The Java package namespace for your connector classes (e.g., io.confluent.connect.example)"
                  />
                  <div className="confluent-input-flex">
                    <TextInput
                      label="Artifact ID *"
                      name="artifactId"
                      placeholder="confluent-connector-example"
                      value={projectDetails.artifactId}
                      onChange={handleInputChange}
                      onBlur={() => handleFieldBlur('artifactId')}
                      showError={shouldShowError('artifactId')}
                      showSuccess={false}
                      errorMessage="Artifact ID is required"
                      tooltip="The unique identifier for your project artifact in Maven/build systems"
                    />
                    <TextInput
                      label="Group ID *"
                      name="groupId"
                      placeholder="io.confluent"
                      value={projectDetails.groupId}
                      onChange={handleInputChange}
                      onBlur={() => handleFieldBlur('groupId')}
                      showError={shouldShowError('groupId')}
                      showSuccess={false}
                      errorMessage="Group ID is required"
                      tooltip="The organization or group that owns this project (usually reverse domain name)"
                    />
                  </div>
                  {projectDetails.connectorLanguageType !== 'python' && (
                    <TextInput
                      label="Project Version *"
                      name="projectVersion"
                      placeholder="1.0.0-SNAPSHOT"
                      value={projectDetails.projectVersion}
                      onChange={handleInputChange}
                      onBlur={() => handleFieldBlur('projectVersion')}
                      showError={shouldShowError('projectVersion')}
                      showSuccess={false}
                      errorMessage="Project Version is required"
                      tooltip="The version number of your connector project (e.g., 1.0.0-SNAPSHOT for development)"
                    />
                  )}
                </div>
              </div>
            )}

            {/* SECTION 3: Build Configuration */}
            {projectDetails.connectorLanguageType && !(projectDetails.connectorType === 'source' && projectDetails.connectorLanguageType === 'python') && (
              <div className="form-section">
                <div className="form-section-header">
                  <div>
                    <h3 className="form-section-title">Build Configuration</h3>
                    <p className="form-section-description">Select versions and build tools for your project</p>
                  </div>
                </div>
                <div className="form-section-content">
                  <div className="confluent-input-flex">
                    <SelectedInput
                      id="javaVersion"
                      label={projectDetails.connectorLanguageType === 'python' ? 'Python Version *' : 'Java Version *'}
                      value={projectDetails.javaVersion}
                      onChange={handleInputChange}
                      onBlur={() => handleFieldBlur('javaVersion')}
                      disabled={projectDetails.connectorLanguageType === 'python'}
                      options={
                        projectDetails.connectorLanguageType === 'python'
                          ? [
                              { value: "3.13", label: "3.13" }
                            ]
                          : [
                              { value: "21", label: "21" },
                              { value: "17", label: "17" },
                              { value: "11", label: "11" },
                              { value: "1.8", label: "8" }
                            ]
                      }
                      showError={shouldShowError('javaVersion')}
                      showSuccess={false}
                      errorMessage={projectDetails.connectorLanguageType === 'python' ? 'Python Version is required' : 'Java Version is required'}
                    />
                    <SelectedInput
                      id="kafkaVersion"
                      label="Kafka Version *"
                      value={projectDetails.kafkaVersion}
                      onChange={handleInputChange}
                      onBlur={() => handleFieldBlur('kafkaVersion')}
                      disabled={projectDetails.connectorLanguageType === 'python'}
                      searchable={projectDetails.connectorLanguageType !== 'python'}
                      options={
                        projectDetails.connectorLanguageType === 'python'
                          ? [
                              { value: "4.2.0", label: "4.2.0" }
                            ]
                          : [
                              { value: "4.2.0", label: "4.2.0" },
                              { value: "4.1.1", label: "4.1.1" },
                              { value: "4.0.1", label: "4.0.1" },
                              { value: "3.9.2", label: "3.9.2" },
                              { value: "3.8.0", label: "3.8.0" },
                              { value: "3.7.0", label: "3.7.0" },
                              { value: "3.6.0", label: "3.6.0" },
                              { value: "3.5.0", label: "3.5.0" }
                            ]
                      }
                      showError={shouldShowError('kafkaVersion')}
                      showSuccess={false}
                      errorMessage="Kafka Version is required"
                    />
                  </div>
                  {projectDetails.connectorLanguageType !== 'python' && (
                    <div className="confluent-input-flex">
                      <SelectedInput
                        id="buildType"
                        label="Build Type *"
                        value={projectDetails.buildType}
                        onChange={handleInputChange}
                        onBlur={() => handleFieldBlur('buildType')}
                        options={[
                          { value: "", disabled: true, label: "Select Build Type" },
                          { value: "maven", label: "Maven" }
                        ]}
                        showError={shouldShowError('buildType')}
                        showSuccess={false}
                        errorMessage="Build Type is required"
                      />
                      {projectDetails.buildType === 'maven' && (
                        <SelectedInput
                          id="mavenVersion"
                          label="Maven Version *"
                          value={projectDetails.mavenVersion}
                          onChange={handleInputChange}
                          onBlur={() => handleFieldBlur('mavenVersion')}
                          options={[
                            { value: "3.9.8", label: "3.9.8" },
                            { value: "3.8.4", label: "3.8.4" },
                            { value: "3.6.3", label: "3.6.3" }
                          ]}
                          showError={shouldShowError('mavenVersion')}
                          showSuccess={false}
                          errorMessage="Maven Version is required"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* SECTION 4: Additional Information */}
            {projectDetails.connectorLanguageType && (
              <div className="form-section">
                <div className="form-section-header">
                  <div>
                    <h3 className="form-section-title">Additional Information</h3>
                    <p className="form-section-description">Provide project description, author, and license details</p>
                  </div>
                </div>
                <div className="form-section-content">
                  <TextInput
                    label="Description *"
                    name="description"
                    placeholder="A generic connector project"
                    value={projectDetails.description}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('description')}
                    showError={shouldShowError('description')}
                    showSuccess={false}
                    errorMessage="Description is required"
                  />
                  <TextInput
                    label="Author *"
                    name="author"
                    placeholder="confluent-developer"
                    value={projectDetails.author}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('author')}
                    showError={shouldShowError('author')}
                    showSuccess={false}
                    errorMessage="Author is required"
                  />
                  <SelectedInput
                    id="license"
                    label="License *"
                    value={projectDetails.license}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('license')}
                    options={[
                      { value: 'apache2', label: 'Apache License 2.0' },
                      { value: 'mit', label: 'MIT License' },
                      { value: 'gpl3', label: 'GNU GPLv3' },
                      { value: 'bsd', label: 'BSD License' }
                    ]}
                    showError={shouldShowError('license')}
                    showSuccess={false}
                    errorMessage="License is required"
                  />
                </div>
              </div>
            )}

            {/* Check the missing Fields*/}
            {projectDetails.connectorLanguageType !== '' && (
              <div>
                {submitAttempted && missingFields.length > 0 && (
                  <div className="missing-fields">
                    Missing: {missingFields.map(field => fieldLabels[field] || field).join(', ')}
                  </div>
                )}
                <div className="button-row">
                  <button
                    className={`confluent-btn ${areDetailsComplete && !isGenerating ? '' : 'disabled-button'}`}
                    onClick={handleGenerateClick}
                    disabled={!areDetailsComplete || isGenerating}
                  >
                    Confirm Details
                  </button>
                  <button
                    className="confluent-btn-secondary"
                    onClick={handleReset}
                    disabled={isGenerating}
                    type="button"
                  >
                    Reset Form
                  </button>
                </div>
                {downloadStatus === 'error' && (
                  <div className="status-message error-message">
                    <div>✗ {errorMessage}</div>
                    <button
                      className="dismiss-error-btn"
                      onClick={() => {
                        setDownloadStatus(null);
                        setErrorMessage('');
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="modal-overlay" onClick={handleCancelDownload}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Confirm Project Generation</h2>
            <div className="modal-body">
              <p>You are about to generate and download the following project:</p>
              <div className="modal-details">
                <div className="modal-detail-item">
                  <strong>Project Name:</strong> {projectDetails.connectorName}
                </div>
                <div className="modal-detail-item">
                  <strong>Type:</strong> {projectDetails.connectorType} connector
                </div>
                <div className="modal-detail-item">
                  <strong>Language:</strong> {projectDetails.connectorLanguageType === 'python' ? 'Python' : 'Java'} {projectDetails.javaVersion}
                </div>
                {projectDetails.connectorLanguageType !== 'python' && (
                  <div className="modal-detail-item">
                    <strong>Build Tool:</strong> {projectDetails.buildType === 'maven' ? 'Maven' : 'Gradle'}
                  </div>
                )}
                <div className="modal-detail-item">
                  <strong>Package:</strong> {projectDetails.packageName}
                </div>
              </div>
              <p className="modal-note">This will download a ZIP file containing your connector project.</p>
            </div>
            <div className="modal-actions">
              <button
                className="confluent-btn"
                onClick={handleConfirmDownload}
              >
                Generate & Download
              </button>
              <button
                className="confluent-btn-secondary"
                onClick={handleCancelDownload}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratorForm;
